/**
 * Image preprocessing utilities for improving barcode/Data Matrix decode rates.
 *
 * Worn, low-contrast, or poorly-lit Data Matrix codes often fail to decode
 * with default image processing. These utilities create preprocessed variants
 * that increase the chance of successful decode — similar to the approach
 * used in the Flutter (H3Kit) app with libdmtx.
 */

/**
 * Convert ImageData to grayscale with enhanced contrast.
 * Applies simple histogram stretching to maximize contrast.
 */
export function enhanceContrast(imageData: ImageData): ImageData {
  const { width, height, data } = imageData;
  const output = new Uint8ClampedArray(data.length);

  // First pass: find min/max luminance
  let minLum = 255;
  let maxLum = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    if (lum < minLum) minLum = lum;
    if (lum > maxLum) maxLum = lum;
  }

  const range = maxLum - minLum;
  if (range === 0) return imageData;

  // Second pass: stretch histogram
  const scale = 255 / range;
  for (let i = 0; i < data.length; i += 4) {
    const lum = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    const stretched = Math.round((lum - minLum) * scale);
    output[i] = stretched;
    output[i + 1] = stretched;
    output[i + 2] = stretched;
    output[i + 3] = 255;
  }

  return new ImageData(output, width, height);
}

/**
 * Invert image colors. Useful for Data Matrix codes printed as
 * light-on-dark (inverted polarity), which are common on metallic
 * surfaces and laser-etched tags.
 */
export function invertImage(imageData: ImageData): ImageData {
  const { width, height, data } = imageData;
  const output = new Uint8ClampedArray(data.length);

  for (let i = 0; i < data.length; i += 4) {
    output[i] = 255 - data[i];
    output[i + 1] = 255 - data[i + 1];
    output[i + 2] = 255 - data[i + 2];
    output[i + 3] = 255;
  }

  return new ImageData(output, width, height);
}

/**
 * Generate multiple preprocessed variants of an image for decode attempts.
 * Returns the original plus enhanced variants.
 */
export function getPreprocessedVariants(imageData: ImageData): ImageData[] {
  return [
    imageData,                    // Original
    enhanceContrast(imageData),   // Contrast-enhanced grayscale
    invertImage(imageData),       // Inverted (for light-on-dark DM codes)
  ];
}
